/**
 * Travel Agent Licensing by State — /travel-agent-licensing-by-state
 * ---------------------------------------------------------------------------
 * SEO hub. State-by-state Seller of Travel licensing index that ranks for
 * "[state] travel agent licensing" queries and routes research traffic into
 * the per-state detail pages at /become-an-agent/{slug}.
 *
 * Editorial register: neutral, informational. The recruiting pitch lives on
 * the detail pages and on /become-an-agent — this page is here to be the
 * best one-page reference on US Seller of Travel rules.
 */

import PublicLayout from '@/layouts/public-layout';
import { jsonForHtml } from '@/lib/json-for-html';
import { Head, Link } from '@inertiajs/react';
import { ArrowUpRight } from 'lucide-react';

const stateOutlines = import.meta.glob('../../svgs/states/*.svg', {
    query: '?raw',
    import: 'default',
    eager: true,
}) as Record<string, string>;

function getStateOutline(slug: string): string | null {
    const key = Object.keys(stateOutlines).find((k) => k.endsWith(`/${slug}.svg`));
    return key ? stateOutlines[key] : null;
}

function StateOutline({ slug, className }: { slug: string; className?: string }) {
    const svg = getStateOutline(slug);
    if (!svg) return null;
    // Fixed-width container so wide states (Alaska, Texas, Tennessee) don't shove
    // adjacent text out of column alignment. SVG inside fits via max-w/max-h.
    // nosemgrep: react-dangerouslysetinnerhtml-no-sanitize — compiled state-outline SVG chosen from an import.meta.glob(?raw) asset set, never supplied markup
    return (
        <span
            aria-hidden="true"
            className={`inline-flex h-5 w-7 shrink-0 items-center justify-center text-current [&>svg]:max-h-full [&>svg]:max-w-full [&>svg]:h-auto [&>svg]:w-auto ${className ?? ''}`}
            dangerouslySetInnerHTML={{ __html: svg }}
        />
    );
}

interface StateRow {
    state: string;
    slug: string;
    sot_required: boolean;
    sot_fee_dollars: number | null;
    sot_program_name: string | null;
    sot_regulator_name: string | null;
    sot_regulator_url: string | null;
    regulatory_note: string | null;
    regulatory_note_source_label: string | null;
    bls_employment: number | null;
    bls_mean_wage: number | null;
    bls_data_year: number | null;
    last_reviewed_at: string | null;
    indexable: boolean;
}

const SOT_STATES = ['California', 'Florida', 'Hawaii', 'Washington'];

function formatNumber(n: number | null): string {
    if (n == null) return '—';
    return n.toLocaleString('en-US');
}

function formatCurrency(n: number | null): string {
    if (n == null) return '—';
    return `$${n.toLocaleString('en-US')}`;
}

function formatReviewed(iso: string | null): string {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
}

export default function TravelAgentLicensingByState({ states, pageTitle }: { states: StateRow[]; pageTitle?: string }) {
    const sotCount = states.filter((s) => s.sot_required).length;
    const notesCount = states.filter((s) => !s.sot_required && s.regulatory_note).length;
    const latestReview = states
        .map((s) => s.last_reviewed_at)
        .filter((d): d is string => Boolean(d))
        .sort()
        .reverse()[0];

    // Schema.org ItemList — gives Google a structured list of every state entry.
    const schema = {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: 'US Travel Agent Licensing by State',
        itemListOrder: 'https://schema.org/ItemListOrderAscending',
        numberOfItems: states.length,
        itemListElement: states.map((s, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            url: `https://www.luxrallytravel.com/become-an-agent/${s.slug}`,
            name: `${s.state} Travel Agent Licensing`,
        })),
    };

    // SEO meta (description, canonical, OG, Twitter) is rendered server-side
    // from the route's view()->share('metadata', ...) → app.blade.php. Inertia's
    // <Head> only carries the title here; meta children would double-emit
    // post-hydration since blade-rendered meta have no head-key to merge.
    // Year comes from config('seo.guide_year') via the pageTitle prop.
    const headTitle = pageTitle ?? 'Travel Agent Licensing by State | LuxRally Travel';

    return (
        <PublicLayout>
            <Head title={headTitle} />
            <script
                type="application/ld+json"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: jsonForHtml(schema) }}
            />

            {/* Hero — editorial intro */}
            <section className="bg-[#0b2222]">
                <div className="mx-auto max-w-4xl px-6 py-20 text-center text-[#E8E0D8] sm:py-24 lg:py-28">
                    <p className="lux-caps lux-caps-sm text-[#E8E0D8]/55">Reference</p>
                    <h1
                        className="font-didot mt-4 text-[#E8E0D8]"
                        style={{ fontSize: 'clamp(2.25rem, 4.5vw, 3.5rem)', lineHeight: 1.1 }}
                    >
                        Travel Agent Licensing by State
                    </h1>
                    <p
                        className="font-didot mx-auto mt-6 max-w-2xl text-[#E8E0D8]/85 italic"
                        style={{
                            fontSize: 'clamp(1.05rem, 1.4vw, 1.3rem)',
                            lineHeight: 1.5,
                        }}
                    >
                        A state-by-state index of Seller of Travel licensing rules, regulator
                        contacts, and Bureau of Labor Statistics employment data for U.S. travel
                        advisors.
                    </p>
                    <p className="mx-auto mt-6 max-w-2xl text-sm leading-[1.7] text-[#E8E0D8]/65">
                        <strong className="font-semibold text-[#E8E0D8]">{sotCount} states</strong>{' '}
                        ({SOT_STATES.join(', ')}) require a Seller of Travel registration to{' '}
                        <strong className="font-semibold text-[#E8E0D8]">
                            sell travel legally to residents of that state
                        </strong>{' '}
                        &mdash; regardless of where the agent is based. A Texas-based advisor
                        booking a California client triggers California&rsquo;s rule, not
                        Texas&rsquo;s.{' '}
                        <strong className="font-semibold text-[#E8E0D8]">
                            {notesCount} additional states
                        </strong>{' '}
                        have notable advertising or trust-account rules that fall short of a full
                        registration. The remainder leave travel-agent activity to general
                        consumer-protection law.
                    </p>
                </div>
            </section>

            {/* Index — desktop table + mobile cards */}
            <section className="bg-[#F5F5F5]">
                <div className="mx-auto max-w-6xl px-6 py-16 sm:py-20 lg:py-24">
                    {/* Desktop table */}
                    <div className="hidden lg:block">
                        <table className="w-full text-left">
                            <thead>
                                <tr className="border-b border-[#0e2727]/15 [&>th]:lux-caps [&>th]:lux-caps-sm [&>th]:pb-4 [&>th]:text-[#0e2727]/55 [&>th]:font-normal">
                                    <th className="text-left">State</th>
                                    <th className="text-left">Seller of Travel</th>
                                    <th className="text-right">Annual fee</th>
                                    <th className="text-right">Travel agents</th>
                                    <th className="text-right">Mean annual wage</th>
                                    <th aria-label="Detail page link" />
                                </tr>
                            </thead>
                            <tbody className="text-[#0e2727]">
                                {states.map((s) => (
                                    <tr
                                        key={s.slug}
                                        className="group border-b border-[#0e2727]/10 transition-colors hover:bg-[#0e2727]/[0.04]"
                                    >
                                        <td className="py-4 pr-4">
                                            <Link
                                                href={`/become-an-agent/${s.slug}`}
                                                className="inline-flex items-center gap-3 font-didot text-[#0b2222] underline-offset-4 group-hover:underline"
                                                style={{ fontSize: 'clamp(1.1rem, 1.3vw, 1.25rem)' }}
                                            >
                                                <StateOutline
                                                    slug={s.slug}
                                                    className="text-[#0b2222]/55"
                                                />
                                                {s.state}
                                            </Link>
                                        </td>
                                        <td className="py-4 pr-4">
                                            <SotCell row={s} />
                                        </td>
                                        <td className="py-4 pr-4 text-right tabular-nums">
                                            {s.sot_required && s.sot_fee_dollars != null
                                                ? `${formatCurrency(s.sot_fee_dollars)}/yr`
                                                : '—'}
                                        </td>
                                        <td className="py-4 pr-4 text-right tabular-nums">
                                            {s.bls_employment != null ? `~${formatNumber(s.bls_employment)}` : '—'}
                                        </td>
                                        <td className="py-4 pr-4 text-right tabular-nums">
                                            {formatCurrency(s.bls_mean_wage)}
                                        </td>
                                        <td className="py-4 text-right">
                                            <Link
                                                href={`/become-an-agent/${s.slug}`}
                                                aria-label={`Read more about ${s.state}`}
                                                className="lux-caps lux-caps-sm inline-flex items-center gap-1 text-[#0b2222]/55 transition-colors group-hover:text-[#0b2222]"
                                            >
                                                Read <ArrowUpRight className="h-3 w-3" strokeWidth={1.5} />
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile / tablet — card list */}
                    <ul className="space-y-4 lg:hidden">
                        {states.map((s) => (
                            <li
                                key={s.slug}
                                className="border border-[#0e2727]/10 bg-white p-5"
                            >
                                <Link
                                    href={`/become-an-agent/${s.slug}`}
                                    className="block"
                                    aria-label={`${s.state} travel agent licensing details`}
                                >
                                    <div className="flex items-baseline justify-between gap-3">
                                        <div className="flex items-center gap-3">
                                            <StateOutline
                                                slug={s.slug}
                                                className="!h-7 !w-9 text-[#0b2222]/65"
                                            />
                                            <h2
                                                className="font-didot text-[#0b2222]"
                                                style={{ fontSize: 'clamp(1.25rem, 2vw, 1.5rem)' }}
                                            >
                                                {s.state}
                                            </h2>
                                        </div>
                                        <ArrowUpRight
                                            className="h-4 w-4 shrink-0 text-[#0b2222]/55"
                                            strokeWidth={1.5}
                                        />
                                    </div>
                                    <div className="mt-2">
                                        <SotCell row={s} />
                                    </div>
                                    <dl className="mt-4 grid grid-cols-3 gap-x-4 gap-y-2 text-xs text-[#0e2727]/65 [&_dd]:tabular-nums [&_dd]:text-[#0e2727] [&_dd]:font-medium">
                                        <div>
                                            <dt className="lux-caps lux-caps-sm text-[#0e2727]/45">
                                                Fee
                                            </dt>
                                            <dd className="mt-1">
                                                {s.sot_required && s.sot_fee_dollars != null
                                                    ? `${formatCurrency(s.sot_fee_dollars)}/yr`
                                                    : '—'}
                                            </dd>
                                        </div>
                                        <div>
                                            <dt className="lux-caps lux-caps-sm text-[#0e2727]/45">
                                                Agents
                                            </dt>
                                            <dd className="mt-1">
                                                {s.bls_employment != null
                                                    ? `~${formatNumber(s.bls_employment)}`
                                                    : '—'}
                                            </dd>
                                        </div>
                                        <div>
                                            <dt className="lux-caps lux-caps-sm text-[#0e2727]/45">
                                                Wage
                                            </dt>
                                            <dd className="mt-1">{formatCurrency(s.bls_mean_wage)}</dd>
                                        </div>
                                    </dl>
                                </Link>
                            </li>
                        ))}
                    </ul>

                    {/* Footnote */}
                    <p className="mt-10 text-xs leading-[1.7] text-[#0e2727]/55">
                        Seller of Travel column reflects state law as of {latestReview ? formatReviewed(latestReview) : 'the most recent review'}. Fee figures are annual unless noted. Travel-agent counts and wages from the U.S. Bureau of Labor Statistics OEWS (May 2024). State law changes frequently &mdash; consult the linked regulator or a qualified attorney before relying on this information.
                    </p>
                </div>
            </section>

            {/* Closing — gentle hand-off to the program */}
            <section className="bg-[#fdfaf3]">
                <div className="mx-auto max-w-2xl px-6 py-16 text-center sm:py-20 lg:py-24">
                    <p className="lux-caps lux-caps-sm text-[#0e2727]/55">
                        Researching the field?
                    </p>
                    <h2
                        className="font-didot mt-4 text-[#0b2222]"
                        style={{ fontSize: 'clamp(1.65rem, 2.4vw, 2.15rem)', lineHeight: 1.15 }}
                    >
                        We make most of this go away.
                    </h2>
                    <p className="mt-5 text-base leading-[1.75] text-[#0e2727]/85">
                        LuxRally is a host agency. We hold every Seller of Travel license required
                        in the United States, so the legal weight stays with us &mdash; not with
                        you. If you&rsquo;re considering a career as a luxury travel advisor, the
                        program walks you through what stays in your hands and what doesn&rsquo;t.
                    </p>
                    <Link
                        href="/become-an-agent"
                        className="lux-caps lux-caps-sm mt-7 inline-block rounded-sm border border-[#0b2222] bg-[#0b2222] px-5 py-2 text-[#E8E0D8] transition-colors duration-300 hover:bg-[#0e2727]"
                    >
                        Explore the program
                    </Link>
                </div>
            </section>
        </PublicLayout>
    );
}

function SotCell({ row }: { row: StateRow }) {
    if (row.sot_required) {
        return (
            <div className="text-sm leading-snug text-[#0e2727]">
                <span className="lux-caps lux-caps-sm text-[#0b2222]">Required</span>
                {row.sot_program_name && (
                    <p className="mt-1 text-[#0e2727]/70">{row.sot_program_name}</p>
                )}
                {row.sot_regulator_url && (
                    <a
                        href={row.sot_regulator_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-0.5 inline-flex items-center gap-1 text-xs text-[#0e2727]/55 underline underline-offset-2 hover:text-[#0b2222]"
                    >
                        {row.sot_regulator_name || 'Regulator'}{' '}
                        <ArrowUpRight className="h-3 w-3" strokeWidth={1.5} />
                    </a>
                )}
            </div>
        );
    }

    if (row.regulatory_note) {
        return (
            <div className="text-sm leading-snug text-[#0e2727]">
                <span className="lux-caps lux-caps-sm text-[#7C4D2E]">Notable rules</span>
                {row.regulatory_note_source_label && (
                    <p className="mt-1 text-xs text-[#0e2727]/65">
                        {row.regulatory_note_source_label}
                    </p>
                )}
            </div>
        );
    }

    return (
        <span className="lux-caps lux-caps-sm text-[#0e2727]/45">Not required</span>
    );
}
